import { Injectable } from '@angular/core';

import { BetslipBarConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CalculatedOdds, emptyCalculatedOdds } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserStateService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { isAccaBoostTokenSelector } from 'packages/sports/web/app/src/acca-boost-base/acca-boost-utils';
import { BetBuilderConfigService } from 'packages/sports/web/app/src/bet-builder/services/bet-builder-config.service';
import { Observable, combineLatest, map, of, switchMap, withLatestFrom } from 'rxjs';

import { BetslipType } from '../../../core/betslip-type';
import { PickOddsState } from '../../../core/picks/pick-models';
import { BetslipGeneratorContextType } from '../../bet-generation/models';
import BetslipPickActions from '../../picks/actions';
import { betslipPicksListCountSelectorIncludeBetbuilderLegs, betslipPicksListSelector } from '../../picks/selectors';
import { IBetslipSummary } from '../../summary/models';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import { comboPickCountSelector, selectedPicksByCurrentTypeSelector, singlePickCountSelector } from '../selectors';
import { BetslipBarRemovePickService } from './betslip-bar-remove-pick.service';
import { BetslipBarReturnsMessageService } from './betslip-bar-returns-message.service';
import { BetBarErrorTracking } from './betslip-bar-tracking.service';

export type BetslipBarTranslations = Sitecore['betslip']['betslipBar'];

export interface BetslipBarModel {
    returnsMessage: string | undefined;
    odds: CalculatedOdds;
    hasBoostedOdds: boolean;
    errorMessage: string | undefined;
    showUndo?: boolean;
    betBuilderBadge?: string;
    isMulti: boolean;
    pickCount: number;
    modelError: BetBarErrorTracking | undefined;
    //this is added for error tracking reasons PickCount is not reliable during group creation
    comboBetPickCount?: number;
}

@Injectable()
export class BetslipBarService {
    private readonly selectedPickCount$: Observable<number> = this.store
        .select(betslipCurrentTypeSelector)
        .pipe(
            switchMap((currentType) =>
                currentType === BetslipType.Combo ? this.store.select(comboPickCountSelector) : this.store.select(singlePickCountSelector),
            ),
        );

    readonly pickListCount$ = this.store.select(betslipPicksListCountSelectorIncludeBetbuilderLegs);
    private readonly accaBoost$ = this.store.select(isAccaBoostTokenSelector);
    private readonly currentType$ = this.store.select(betslipCurrentTypeSelector);

    private readonly selectedPicks$ = this.store.select(selectedPicksByCurrentTypeSelector);

    private readonly translations: BetslipBarTranslations;
    private readonly allPicks$ = this.store.select(betslipPicksListSelector);

    private readonly validModel$: Observable<BetslipBarModel> = this.summaryService.getSummary$(BetslipGeneratorContextType.BetslipBar).pipe(
        withLatestFrom(this.allPicks$, this.accaBoost$, this.selectedPickCount$, this.currentType$),
        map(([[, summary], allPicks, hasAccaBoost, pickCount, currentType]) => {
            const boostedOdds = this.tryGetBoostedOdds(summary);
            const { betBuilderBadge } = this.getBetBuilderBadge(summary);

            return {
                returnsMessage: this.betslipBarReturns.getReturnsMessage(summary, allPicks.length, hasAccaBoost, currentType),
                odds: boostedOdds ?? summary.totalOdds,
                hasBoostedOdds: !!boostedOdds,
                errorMessage: undefined,
                pickCount,
                isMulti: allPicks.length > 1,
                betBuilderBadge,
                modelError: undefined,
            };
        }),
    );

    readonly model$ = combineLatest([this.selectedPickCount$, this.selectedPicks$, this.currentType$, this.removePickService.errorModel$]).pipe(
        switchMap(([pickCount, selectedPicks, currentType, errorModel]) => {
            if (
                (selectedPicks.some((pick) => pick.oddsState !== PickOddsState.Open) && currentType === BetslipType.Single) ||
                !selectedPicks.length
            ) {
                return of(this.getEmptyModel(pickCount));
            }

            const validSingleBet = currentType === BetslipType.Single && selectedPicks.length === 1;
            const validComboBet = currentType === BetslipType.Combo && !errorModel.hasError;
            const betBuilderCombo = currentType === BetslipType.Combo && this.betslipBarConfig.isAngstromSGPEnabled;

            if (errorModel.hasError && betBuilderCombo && pickCount > 1) {
                const emptyModel = this.getEmptyModel(pickCount);

                return of({
                    ...emptyModel,
                    errorMessage: errorModel.errorMessage,
                    showUndo: errorModel.showUndo,
                    modelError: errorModel.trackError,
                    comboBetPickCount: errorModel.comboBetPickCount,
                });
            }

            if (validSingleBet || validComboBet) {
                return this.validModel$;
            }

            return of(this.getEmptyModel(pickCount));
        }),
    );

    readonly oddsDisplayFormat$ = this.userStateService.userState$().pipe(map((state) => state.oddsFormat ?? OddsDisplayFormat.EU));

    constructor(
        private betslipBarReturns: BetslipBarReturnsMessageService,
        private summaryService: BetslipSummaryService,
        private store: Store,
        private removePickService: BetslipBarRemovePickService,
        private betBuilderConfigService: BetBuilderConfigService,
        { betslip: { betslipBar } }: Sitecore,
        private userStateService: UserStateService,
        private betslipBarConfig: BetslipBarConfig,
    ) {
        this.translations = betslipBar;
    }

    private getEmptyModel(pickCount: number): BetslipBarModel {
        return {
            odds: emptyCalculatedOdds,
            returnsMessage: undefined,
            hasBoostedOdds: false,
            errorMessage: undefined,
            pickCount,
            isMulti: false,
            modelError: undefined,
        };
    }

    private getBetBuilderBadge({ hasBetBuilder, missingBetBuilderLegs }: IBetslipSummary): {
        betBuilderBadge?: string;
    } {
        if (!this.betBuilderConfigService.showEmbeddedUi() || !hasBetBuilder || missingBetBuilderLegs) {
            return {};
        }

        return { betBuilderBadge: this.translations.BetBuilderBadge };
    }

    private tryGetBoostedOdds({ slipSummaries = [] }: IBetslipSummary): CalculatedOdds | undefined {
        return slipSummaries[0]?.rewardTokenModifications?.oddsBoost?.boostedOdds;
    }

    removeLastLeg() {
        this.store.dispatch(BetslipPickActions.removeLastPickFromPickList());
    }
}
